// hooks/use-paginated-feed.ts
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ListingResult,
  FeedFilterState,
  ListingFeedResponse,
} from "@kraahl/contract";
import { fetchListingFeed } from "@/lib/search/fetch-listing-feed";
import {
  buildFeedSnapshotKey,
  parseFeedSnapshot,
  serializeFeedSnapshot,
  selectSnapshotEvictions,
  FEED_SNAPSHOT_PREFIX,
  FEED_SNAPSHOT_TTL_MS,
  type FeedSnapshot,
} from "@/lib/feed/feed-snapshot";

// Single source of truth lives in @kraahl/contract. `ListingItem` kept as a
// local alias for the many existing consumers; FeedFilterState re-exported.
export type ListingItem = ListingResult;
export type { FeedFilterState };

type FeedResponse = ListingFeedResponse;

interface UsePaginatedFeedOptions {
  userId?: string;
  mode?: "just-listed" | "discover" | "recommended";
  filters?: FeedFilterState;
  limit?: number;
  searchQuery?: string;
  enabled?: boolean;
  slugBrand?: string;
  slugTheme?: string;
  slugProductType?: string;
  debug?: boolean;
  sortBy?: string | null;
  maxItems?: number | null;
  /**
   * Opt-in sessionStorage snapshot cache. When set, the accumulated feed
   * (pages, items, cursor, scroll) is persisted per query-param combo and
   * hydrated synchronously on the next mount (stale-while-revalidate). A
   * stable namespace for the consumer instance, e.g. "live". Omit → no caching
   * (identical to the previous behavior). @see lib/feed/feed-snapshot.ts
   */
  cacheKey?: string;
  /** CSS selector of the scroll container whose offset is saved/restored with
   *  the snapshot. Only used when `cacheKey` is set. */
  scrollRootSelector?: string;
  /** Override the snapshot freshness window (ms). Defaults to ~3 min. */
  snapshotTtlMs?: number;
}

interface FetchOptions {
  replace?: boolean;
  preservePageState?: boolean;
}

const DEFAULT_LIMIT = 16;
const DEFAULT_MAX_ITEMS = 60;

export function usePaginatedFeed({
  userId,
  mode = "just-listed",
  filters,
  limit = DEFAULT_LIMIT,
  searchQuery,
  enabled = true,
  slugBrand,
  slugTheme,
  slugProductType,
  debug = false,
  sortBy,
  maxItems: maxItemsOption = DEFAULT_MAX_ITEMS,
  cacheKey,
  scrollRootSelector = "[data-feed-scroll-root]",
  snapshotTtlMs = FEED_SNAPSHOT_TTL_MS,
}: UsePaginatedFeedOptions) {
  const maxItems = maxItemsOption;

  const normalizedSearchQuery = useMemo(() => searchQuery?.trim() || "", [searchQuery]);
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        filters: filters || {},
        searchQuery: normalizedSearchQuery,
        slugBrand: slugBrand ?? "",
        slugTheme: slugTheme ?? "",
        slugProductType: slugProductType ?? "",
        sortBy: sortBy ?? null,
      }),
    [filters, normalizedSearchQuery, slugBrand, slugTheme, slugProductType, sortBy]
  );

  // sessionStorage snapshot cache (opt-in via `cacheKey`). The key embeds every
  // query parameter, so a different filter/sort/search combo is a different
  // entry (task req 4: invalidation-by-key is free). Null when caching is off →
  // every branch below no-ops and the hook behaves exactly as before.
  // @see lib/feed/feed-snapshot.ts
  const snapshotStorageKey = useMemo(
    () => (cacheKey ? buildFeedSnapshotKey(cacheKey, { mode, userId, limit, filtersKey }) : null),
    [cacheKey, mode, userId, limit, filtersKey]
  );

  // Read the snapshot for the INITIAL query exactly once, during first render,
  // so the useState lazy initializers below hydrate synchronously (task req 2:
  // instant paint, no skeleton). Guarded for SSR; feed consumers mount
  // client-only (behind the isDesktop gate) so there's no hydration mismatch.
  const initialSnapshotRef = useRef<FeedSnapshot | null | undefined>(undefined);
  if (initialSnapshotRef.current === undefined) {
    initialSnapshotRef.current =
      typeof window !== "undefined" && snapshotStorageKey
        ? parseFeedSnapshot(window.sessionStorage.getItem(snapshotStorageKey), Date.now(), snapshotTtlMs)
        : null;
  }

  const [items, setItems] = useState<ListingItem[]>(() => initialSnapshotRef.current?.items ?? []);
  const [page, setPage] = useState(() => initialSnapshotRef.current?.page ?? 0);
  const [hasNextPage, setHasNextPage] = useState(() => initialSnapshotRef.current?.hasNextPage ?? true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(() => initialSnapshotRef.current != null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadMoreLockRef = useRef(false);
  const nextCursorRef = useRef<string | null>(initialSnapshotRef.current?.nextCursor ?? null);

  // Snapshot bookkeeping (all inert when caching is off).
  const didHydrateFromSnapshotRef = useRef(initialSnapshotRef.current != null);
  const scrollTopRef = useRef(initialSnapshotRef.current?.scrollTop ?? 0);
  const scrollRootSelectorRef = useRef(scrollRootSelector);
  scrollRootSelectorRef.current = scrollRootSelector;

  const fetchPage = useCallback(
    async (targetPage: number, options: FetchOptions = {}) => {
      if (!enabled) {
        return;
      }
      const { replace = false, preservePageState = false } = options;

      if (replace && mode === "recommended") nextCursorRef.current = null;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: targetPage.toString(),
          limit: limit.toString(),
        });

        if (userId) {
          params.set("userId", userId);
        }
        if (mode === "recommended" && nextCursorRef.current) {
          params.set("cursor", nextCursorRef.current);
        }
        if (mode === "recommended" && debug) {
          params.set("debug", "true");
        }
        if (mode === "discover") {
          params.set("discover", "true");
        }
        if (filters) {
          if (filters.brands?.length) {
            params.set("brands", filters.brands.join(","));
          }
          if (filters.themes?.length) {
            params.set("themes", filters.themes.join(","));
          }
          if (filters.categories?.length) {
            params.set("categories", filters.categories.join(","));
          }
          if (filters.tcgs?.length) {
            params.set("tcgs", filters.tcgs.join(","));
          }
          if (filters.conditions?.length) {
            params.set("conditions", filters.conditions.join(","));
          }
          if (filters.gradeRange) {
            params.set("gradeMin", filters.gradeRange[0].toString());
            params.set("gradeMax", filters.gradeRange[1].toString());
          }
          if (filters.listingFormats?.length) {
            params.set("listingFormats", filters.listingFormats.join(","));
          }
          if (filters.tcgSets?.length) {
            params.set("tcgSets", filters.tcgSets.join(","));
          }
          if (filters.tcgRarities?.length) {
            params.set("tcgRarities", filters.tcgRarities.join(","));
          }
          if (filters.cardConditions?.length) {
            params.set("cardConditions", filters.cardConditions.join(","));
          }
          if (filters.cardLanguages?.length) {
            params.set("cardLanguages", filters.cardLanguages.join(","));
          }
          if (filters.gradingCompanies?.length) {
            params.set("gradingCompanies", filters.gradingCompanies.join(","));
          }
          if (filters.cardGradeRange) {
            params.set("cardGradeMin", filters.cardGradeRange[0].toString());
            params.set("cardGradeMax", filters.cardGradeRange[1].toString());
          }
          if (filters.cardFormats?.length) {
            params.set("cardFormats", filters.cardFormats.join(","));
          }
          if (filters.mtgPrintVariants?.length) {
            params.set("mtgPrintVariants", filters.mtgPrintVariants.join(","));
          }
          if (filters.listingCategory) {
            params.set("listingCategory", filters.listingCategory);
          }
        }

        if (normalizedSearchQuery) {
          params.set("q", normalizedSearchQuery);
        }
        if (sortBy) {
          params.set("sortBy", sortBy);
        }
        if (slugBrand) params.set("brand", slugBrand);
        if (slugTheme) params.set("theme", slugTheme);
        if (slugProductType) params.set("product_type", slugProductType);

        const data = await fetchListingFeed(mode, params, { signal: controller.signal });
        if (controller.signal.aborted) {
          return;
        }

        setItems((prev) => {
          if (replace) {
            if (!preservePageState) {
              const next = data.items;
              if (maxItems != null && next.length > maxItems) {
                return next.slice(0, maxItems);
              }
              return next;
            }

            const seen = new Set<string>();
            const next: ListingItem[] = [];

            for (const entry of data.items) {
              seen.add(entry.id);
              next.push(entry);
            }

            for (const entry of prev) {
              if (!seen.has(entry.id)) {
                next.push(entry);
              }
            }

            if (maxItems != null && next.length > maxItems) {
              return next.slice(0, maxItems);
            }
            return next;
          }

          const indexById = new Map<string, number>();
          const next = prev.map((entry, idx) => {
            indexById.set(entry.id, idx);
            return entry;
          });

          for (const entry of data.items) {
            const existingIndex = indexById.get(entry.id);
            if (existingIndex !== undefined) {
              next[existingIndex] = { ...next[existingIndex], ...entry };
            } else {
              indexById.set(entry.id, next.length);
              next.push(entry);
            }
          }

          if (maxItems != null && next.length > maxItems) {
            return next.slice(0, maxItems);
          }
          return next;
        });

        if (!preservePageState) {
          setPage(data.page);
        }
        setHasNextPage(data.hasNextPage);
        if (mode === "recommended") {
          nextCursorRef.current = data.nextCursor ?? null;
        }
        setIsInitialized(true);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setError(err instanceof Error ? err.message : "Unknown error");
        // Mark as initialized even on error to prevent infinite retry loops
        setIsInitialized(true);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [enabled, filtersKey, limit, maxItems, mode, normalizedSearchQuery, userId]
  );

  const loadMore = useCallback(() => {
    if (!enabled) {
      return;
    }
    if (loadMoreLockRef.current || isLoading || !hasNextPage) {
      return;
    }
    loadMoreLockRef.current = true;
    fetchPage(page + 1).finally(() => {
      loadMoreLockRef.current = false;
    });
  }, [fetchPage, hasNextPage, isLoading, page]);

  const loadFirstPage = useCallback(() => {
    if (!enabled) {
      return;
    }
    fetchPage(1, { replace: true });
  }, [enabled, fetchPage]);

  const refetchFirstPage = useCallback(() => {
    if (!enabled) {
      return;
    }
    fetchPage(1, { replace: true, preservePageState: true });
  }, [enabled, fetchPage]);

  const updateItem = useCallback((id: string, patch: Partial<ListingItem>) => {
    setItems(prev => {
      const idx = prev.findIndex(item => item.id === id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...prev[idx], ...patch };
      return next;
    });
  }, []);

  const refresh = refetchFirstPage;
  const reroll = useCallback(() => {
    setItems([]);
    setPage(0);
    setHasNextPage(true);
    setIsInitialized(false);
    fetchPage(1, { replace: true });
  }, [fetchPage]);

  const refetchFirstPageRef = useRef(refetchFirstPage);
  refetchFirstPageRef.current = refetchFirstPage;

  /**
   * Write the current accumulated feed to sessionStorage (best-effort). Adapter
   * I/O wrapping the pure `serializeFeedSnapshot` + `selectSnapshotEvictions`
   * domain. No-op unless caching is on and there's something worth persisting.
   */
  const persistSnapshot = useCallback(() => {
    if (typeof window === "undefined" || !snapshotStorageKey) return;
    if (!isInitialized || items.length === 0) return;
    try {
      window.sessionStorage.setItem(
        snapshotStorageKey,
        serializeFeedSnapshot({
          items,
          page,
          hasNextPage,
          nextCursor: nextCursorRef.current,
          scrollTop: scrollTopRef.current,
          ts: Date.now(),
        })
      );
      // LRU-evict oldest entries so the cache can't grow unbounded.
      const entries: { key: string; ts: number }[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (!k || !k.startsWith(FEED_SNAPSHOT_PREFIX)) continue;
        let ts = 0;
        try {
          const parsed = JSON.parse(window.sessionStorage.getItem(k) ?? "{}");
          if (typeof parsed?.ts === "number") ts = parsed.ts;
        } catch {
          /* corrupt entry — treat as oldest */
        }
        entries.push({ key: k, ts });
      }
      for (const k of selectSnapshotEvictions(entries)) {
        window.sessionStorage.removeItem(k);
      }
    } catch {
      // sessionStorage full / disabled (private mode) — cache is best-effort.
    }
  }, [snapshotStorageKey, isInitialized, items, page, hasNextPage]);

  const persistSnapshotRef = useRef(persistSnapshot);
  persistSnapshotRef.current = persistSnapshot;

  /** Drop this query's snapshot (task req 4). Keying already isolates combos;
   *  this is the explicit escape hatch for "force fresh next mount". */
  const invalidate = useCallback(() => {
    if (typeof window === "undefined" || !snapshotStorageKey) return;
    window.sessionStorage.removeItem(snapshotStorageKey);
  }, [snapshotStorageKey]);

  const hasAttemptedLoad = useRef(false);
  const prevFiltersKey = useRef(filtersKey);

  useEffect(() => {
    if (!enabled) {
      abortControllerRef.current?.abort();
      // A caching consumer keeps its hydrated content while disabled (e.g. a
      // prefs gate) — clearing here would blow away the instant snapshot paint.
      // Non-caching consumers keep the legacy reset-on-disable behavior.
      if (!snapshotStorageKey) {
        setItems([]);
        setPage(0);
        setHasNextPage(true);
        setIsInitialized(false);
        setIsLoading(false);
      }
      return;
    }
    const filtersChanged = prevFiltersKey.current !== filtersKey;
    prevFiltersKey.current = filtersKey;

    if (filtersChanged) {
      hasAttemptedLoad.current = false;
    }

    if (hasAttemptedLoad.current) {
      return;
    }
    hasAttemptedLoad.current = true;

    // (1) Already hydrated from a snapshot for THIS key on mount → stale-while-
    //     revalidate: keep the painted content, refresh the first page in the
    //     background. `refetchFirstPage` merges (replace + preservePageState),
    //     so it can't clobber user-loaded tail pages (task req 5).
    if (snapshotStorageKey && didHydrateFromSnapshotRef.current && !filtersChanged) {
      didHydrateFromSnapshotRef.current = false;
      refetchFirstPage();
      return;
    }

    // (2) Filter/prefs change (or lazy-init missed) → try a synchronous snapshot
    //     hydrate for the current key so the switch paints instantly too, then
    //     revalidate. Scroll is intentionally NOT restored here (a filter switch
    //     expects the top); mount scroll-restore is handled separately below.
    if (snapshotStorageKey) {
      const snap = parseFeedSnapshot(
        typeof window !== "undefined" ? window.sessionStorage.getItem(snapshotStorageKey) : null,
        Date.now(),
        snapshotTtlMs
      );
      if (snap) {
        setItems(snap.items);
        setPage(snap.page);
        setHasNextPage(snap.hasNextPage);
        nextCursorRef.current = snap.nextCursor;
        setError(null);
        setIsInitialized(true);
        refetchFirstPage();
        return;
      }
    }

    // (3) No snapshot → fresh load. Reset stale content first on a filter change.
    if (filtersChanged) {
      setItems([]);
      setPage(0);
      setHasNextPage(true);
      setIsInitialized(false);
    }
    loadFirstPage();
  }, [enabled, filtersKey, snapshotStorageKey, snapshotTtlMs, loadFirstPage, refetchFirstPage, mode, userId]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      hasAttemptedLoad.current = false;
      // Final snapshot write captures the leave-time scroll position so the next
      // mount can restore it (task req 3). Runs before the scroll root is torn
      // down on client navigation, so the live read is still valid.
      if (typeof document !== "undefined") {
        const root = document.querySelector(scrollRootSelectorRef.current) as HTMLElement | null;
        if (root) scrollTopRef.current = root.scrollTop;
      }
      persistSnapshotRef.current();
    };
  }, []);

  // Track the scroll-root offset so persisted snapshots carry an up-to-date
  // scroll position without writing to storage on every scroll tick.
  useEffect(() => {
    if (!snapshotStorageKey || typeof document === "undefined") return;
    const root = document.querySelector(scrollRootSelectorRef.current) as HTMLElement | null;
    if (!root) return;
    scrollTopRef.current = root.scrollTop;
    const onScroll = () => {
      scrollTopRef.current = root.scrollTop;
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, [snapshotStorageKey]);

  // Debounced persist whenever the accumulated feed changes.
  useEffect(() => {
    if (!snapshotStorageKey) return;
    const handle = window.setTimeout(() => persistSnapshotRef.current(), 200);
    return () => window.clearTimeout(handle);
  }, [snapshotStorageKey, items, page, hasNextPage, isInitialized]);

  // Restore scroll after the hydrated content has painted (mount-only, and only
  // when we hydrated from a snapshot — a fresh load starts at the top). Two rAFs
  // wait for the grid to lay out so the scroll target height exists.
  useEffect(() => {
    const snap = initialSnapshotRef.current;
    if (!snap || snap.scrollTop <= 0 || typeof document === "undefined") return;
    const root = document.querySelector(scrollRootSelectorRef.current) as HTMLElement | null;
    if (!root) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        root.scrollTop = snap.scrollTop;
        scrollTopRef.current = snap.scrollTop;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // Mount-only: reads only refs (initialSnapshotRef / scrollRootSelectorRef),
    // which are stable, so there are no reactive deps to declare.
  }, []);

  // Cross-tab invalidation: if another tab removes our entry, revalidate in the
  // background (keep painted content, no reset flash). Mirrors the storage-event
  // sync in use-my-seller-orders.ts.
  useEffect(() => {
    if (typeof window === "undefined" || !snapshotStorageKey) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === snapshotStorageKey && e.newValue === null && e.oldValue !== null) {
        refetchFirstPageRef.current();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [snapshotStorageKey]);

  return {
    items,
    page,
    hasNextPage,
    isLoading,
    isInitialized,
    error,
    loadMore,
    refresh,
    refetchFirstPage,
    reroll,
    updateItem,
    invalidate,
  };
}
